import { getAccessTokenForFetch } from './fetchAuthToken';

const getAuthHeader = async (): Promise<Headers> => {
  const headers = new Headers();

  const accessToken = await getAccessTokenForFetch();

  const bearer = `Bearer ${accessToken}`;

  headers.append('Authorization', bearer);
  headers.append('Content-Type', 'application/json');

  return headers;
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

let reloadInFlight = false;

const handleSessionExpired = () => {
  if (reloadInFlight) return;
  reloadInFlight = true;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('session-expired'));
  }
};

export const internalFetch = async (
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  abortController: AbortController,
  body?: string,
  noJsonResponse?: boolean,
  retry: number = 3
): Promise<any> => {
  const headers = await getAuthHeader();

  const signal = abortController?.signal;
  const options: RequestInit = {
    method: method,
    headers: headers,
    ...(body !== undefined && { body }), // Only include body if defined
    signal: signal,
  };

  for (let attempt = 0; attempt < retry; attempt++) {
    try {
      const response = await fetch(url, options);

      if (response.ok) {
        if (noJsonResponse) {
          return response;
        }
        return await response.json();
      } else {
        console.log(
          `Response status ${response.status} on attempt ${
            attempt + 1
          }/${retry}`
        );

        if (response.status === 403) {
          throw new Error("You don't have permission for this action.");
        }

        if (response.status === 401) {
          const problem = await response
            .clone()
            .json()
            .catch(() => null as { type?: string } | null);
          if (problem?.type === 'TokenExpiredException') {
            handleSessionExpired();
            throw new Error('Session expired. Please log in again.');
          }
          throw new Error(
            'Authentication token expired, please sign in again.'
          );
        }

        const isClientError =
          response.status >= 400 &&
          response.status < 500 &&
          response.status !== 408;

        if (isClientError || attempt === retry - 1) {
          const errorText = await response.text();
          throw new Error(
            `API request failed: ${response.status} - ${errorText}`
          );
        }

        await sleep(500);
      }
    } catch (error: any) {
      // If the request was aborted, don't retry
      if (signal?.aborted) {
        throw new Error('Request aborted');
      }

      // If this is the last attempt, throw the error
      if (attempt === retry - 1) {
        console.error('Fetch error:', error);
        throw error;
      }

      console.log(
        `Fetch failed on attempt ${attempt + 1}/${retry}:`,
        error.message
      );
      await sleep(500);
    }
  }

  throw new Error('Request failed after all retries');
};
